import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen, 
  Target, 
  PlusCircle, 
  Trophy,
  LogOut,
  LayoutDashboard,
  Lightbulb,
  MessageCircle,
  Lock
} from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import { useAuthStore } from '../store/authStore';
import type { Sport } from '../types';
import PageHeader from './layout/PageHeader';
import { Footer } from './layout/Footer';
import QuoteOfTheDay from './QuoteOfTheDay';
import { useSubscription } from '../hooks/useSubscription';
import { subscriptionService, ROOKIE_LIMITS } from '../services/subscriptionService';
import { SubscriptionCard } from './SubscriptionCard';
import UpgradeModal from './UpgradeModal';

// Import sport-specific icons
import FootballIcon from '../assets/icons/football.svg';
import BasketballIcon from '../assets/icons/basketball.svg';
import SoccerIcon from '../assets/icons/soccer.svg';
import BaseballIcon from '../assets/icons/baseball.svg';
import SoftballIcon from '../assets/icons/softball.svg';
import VolleyballIcon from '../assets/icons/volleyball.svg';
import HockeyIcon from '../assets/icons/hockey.svg';
import LacrosseIcon from '../assets/icons/lacrosse.svg';

const Dashboard: React.FC = () => {
  const { currentTeam, allTeams, fetchAllTeams, switchTeam } = useTeamStore();
  const { signOut } = useAuthStore();
  const { isProUser, profile, subscribeToPro, loadProfile } = useSubscription();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // After Stripe checkout success, sync subscription from session_id and refresh profile
  const sessionIdFromUrl = searchParams.get('session_id');
  useEffect(() => {
    if (!sessionIdFromUrl) return;
    let cancelled = false;
    (async () => {
      const ok = await subscriptionService.syncSubscriptionFromSession(sessionIdFromUrl);
      if (cancelled) return;
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      if (ok) await loadProfile();
    })();
    return () => { cancelled = true; };
  }, [sessionIdFromUrl, loadProfile]);

  useEffect(() => {
    const loadTeams = async () => {
      setIsLoading(true);
      await fetchAllTeams();
      setIsLoading(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  // Listen for force refresh events (e.g., after invitation acceptance)
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log('[Dashboard] Received force-team-refresh event, refreshing teams...');
      const loadTeams = async () => {
        setIsLoading(true);
        await fetchAllTeams();
        setIsLoading(false);
      };
      loadTeams();
    };

    window.addEventListener('force-team-refresh', handleForceRefresh);
    return () => window.removeEventListener('force-team-refresh', handleForceRefresh);
  }, [fetchAllTeams]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  if (allTeams.length === 0) {
    return (
      <div>
        <div className="flex justify-end mb-6">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-stone hover:text-dark transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
        <div className="text-center">
          <h2 className="text-h3 mb-4">Welcome to Hustli</h2>
          <p className="mb-6 caption">Get started by setting up your team.</p>
          <Link
            to="/team-setup"
            className="btn-primary"
          >
            Set Up Team
          </Link>
        </div>
      </div>
    );
  }

  const handleTeamClick = async (teamId: string) => {
    if (teamId === currentTeam?.id) {
      // Already viewing this team — go to team hub so user can pick an area
      navigate(`/team/${teamId}`);
      return;
    }

    // Clear all possible caches in localStorage
    localStorage.removeItem('practiceData');
    localStorage.removeItem('drillData');
    if (currentTeam?.id) {
      localStorage.removeItem(`practices_${currentTeam.id}`);
    }
    localStorage.removeItem(`practices_${teamId}`);
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('practices_') || key.startsWith('drills_') || key.startsWith('team_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    const cacheClearEvent = new CustomEvent('clear-team-cache', {
      detail: { previousTeamId: currentTeam?.id, newTeamId: teamId },
    });
    window.dispatchEvent(cacheClearEvent);

    await switchTeam(teamId);
    navigate(`/team/${teamId}`);
  };

  // Get sport icon based on sport type
  const getSportIcon = (sport: Sport) => {
    switch (sport) {
      case 'Football': 
        return <img src={FootballIcon} alt="Football" className="h-5 w-5" />;
      case 'Basketball': 
        return <img src={BasketballIcon} alt="Basketball" className="h-5 w-5" />;
      case 'Soccer': 
        return <img src={SoccerIcon} alt="Soccer" className="h-5 w-5" />;
      case 'Baseball': 
        return <img src={BaseballIcon} alt="Baseball" className="h-5 w-5" />;
      case 'Softball': 
        return <img src={SoftballIcon} alt="Softball" className="h-5 w-5" />;
      case 'Volleyball': 
        return <img src={VolleyballIcon} alt="Volleyball" className="h-5 w-5" />;
      case 'Hockey': 
        return <img src={HockeyIcon} alt="Hockey" className="h-5 w-5" />;
      case 'Lacrosse': 
        return <img src={LacrosseIcon} alt="Lacrosse" className="h-5 w-5" />;
      default: 
        return <Trophy className="h-5 w-5" />;
    }
  };

  // Get team details based on sport type
  const getTeamDetails = (team: any) => {
    switch (team.sport) {
      case 'Football':
      case 'FlagFootball':
        return (
          <>
            <div>Offense: {team.offensiveScheme}</div>
            <div>Defense: {team.defensiveScheme}</div>
          </>
        );
      case 'Basketball':
        return (
          <>
            <div>Offense: {team.offensiveScheme}</div>
            <div>Defense: {team.defensiveScheme}</div>
          </>
        );
      case 'Soccer':
        return (
          <>
            <div>Formation: {team.offensiveScheme}</div>
            <div>Style: {team.defensiveScheme}</div>
          </>
        );
      case 'Baseball':
      case 'Softball':
        return <div>Focus: {team.offensiveScheme}</div>;
      case 'Hockey':
        return <div>System: {team.offensiveScheme}</div>;
      case 'Volleyball':
        return (
          <>
            <div>Offense: {team.offensiveScheme}</div>
            <div>Defense: {team.defensiveScheme}</div>
          </>
        );
      case 'Lacrosse':
        return (
          <>
            <div>Offense: {team.offensiveScheme}</div>
            <div>Defense: {team.defensiveScheme}</div>
          </>
        );
      default:
        return null;
    }
  };

  // For Rookie: show teams sorted by created_at (oldest first); first team is accessible, rest locked
  const sortedTeams = [...allTeams].sort((a, b) => {
    const aAt = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bAt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return aAt - bAt;
  });

  return (
    <div>
      {/* Logout Button */}
      <div className="flex justify-end mb-6">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-stone hover:text-dark transition-colors"
        >
          <LogOut size={18} />
          <span className="text-sm">Logout</span>
        </button>
      </div>

      {/* Two-column layout for larger screens */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="xl:col-span-2">
          {/* Team Selection */}
          <div className="mb-8">
            <PageHeader 
              title="Home"
              icon={<LayoutDashboard className="h-6 w-6" />}
              actions={
                <div className="flex items-center gap-2">
                  {isProUser && (
                    <span className="text-sm text-gray-600">
                      {allTeams.length} team{allTeams.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {!isProUser && allTeams.length >= 1 ? (
                    <button
                      type="button"
                      onClick={() => setShowUpgradeModal(true)}
                      className="flex items-center gap-1.5 py-2 px-4 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200 transition-colors"
                      aria-label="Upgrade to add more teams"
                    >
                      <Lock className="h-4 w-4" />
                      New Team
                    </button>
                  ) : (
                    <Link
                      to="/team-setup"
                      className="flex items-center gap-1 btn-primary py-2 px-4 text-sm"
                    >
                      <PlusCircle className="h-4 w-4" />
                      New Team
                    </Link>
                  )}
                </div>
              }
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4">
              {sortedTeams.map((team, index) => {
                const isLocked = index > 0 && !isProUser;
                if (isLocked) {
                  return (
                    <div
                      key={team.id}
                      className="relative border border-gray-200 rounded-2xl p-4 h-full opacity-60"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 rounded-full bg-gray-200 text-gray-500">
                          {getSportIcon(team.sport)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-600">{team.name}</h3>
                          <p className="text-xs text-gray-500">{team.sport}</p>
                        </div>
                        <Lock className="h-4 w-4 text-gray-400 ml-auto shrink-0" aria-hidden />
                      </div>
                      <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1 text-gray-500">
                        <div>Age: {team.ageLevel}</div>
                        <div>Experience: {team.experienceLevel}</div>
                        {getTeamDetails(team)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={team.id}>
                    <div 
                      onClick={() => handleTeamClick(team.id)}
                      className={`border rounded-2xl p-4 cursor-pointer transition-all duration-200 ${
                        currentTeam?.id === team.id 
                          ? 'border-primary bg-primary bg-opacity-50 shadow-lg scale-[1.02]' 
                          : 'border-stone hover:border-primary hover:bg-cream'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-2 rounded-full ${
                          currentTeam?.id === team.id 
                            ? 'bg-primary text-white' 
                            : 'bg-primary bg-opacity-10 text-primary'
                        }`}>
                          {getSportIcon(team.sport)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-dark">{team.name}</h3>
                          <p className={`text-xs ${
                            currentTeam?.id === team.id ? 'text-dark' : 'text-stone'
                          }`}>{team.sport}</p>
                        </div>
                        {currentTeam?.id === team.id && (
                          <span className="bg-primary text-white text-xs px-2 py-1 rounded-full font-medium">
                            Active
                          </span>
                        )}
                      </div>
                      <div className={`text-xs grid grid-cols-2 gap-x-4 gap-y-1 ${
                        currentTeam?.id === team.id ? 'text-dark' : 'text-stone'
                      }`}>
                        <div>Age: {team.ageLevel}</div>
                        <div>Experience: {team.experienceLevel}</div>
                        {getTeamDetails(team)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quote of the Day */}
          <QuoteOfTheDay />
        </div>

{/* Generation Counts Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              AI Generation Usage
            </h3>
            
            <div className="space-y-4">
              {/* AI Coach messages */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <MessageCircle className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">AI Coach</p>
                    <p className="text-xs text-gray-600">
                      {isProUser ? 'Unlimited' : `${subscriptionService.getAiChatMessageCount(profile || null)}/${ROOKIE_LIMITS.CHAT_MESSAGES} this month`}
                    </p>
                  </div>
                </div>
                {!isProUser && subscriptionService.hasReachedChatLimit(profile || null) && (
                  <div className="text-xs text-red-600 font-medium">Limit Reached</div>
                )}
              </div>
              {/* AI-powered Practice Plans */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">AI-powered Practice Plans</p>
                    <p className="text-xs text-gray-600">
                      {isProUser ? 'Unlimited' : `${subscriptionService.getPracticeGenerationCount(profile || null)}/${ROOKIE_LIMITS.PRACTICE_PLANS} this month`}
                    </p>
                  </div>
                </div>
                {!isProUser && subscriptionService.hasReachedPracticeLimit(profile || null) && (
                  <div className="text-xs text-red-600 font-medium">Limit Reached</div>
                )}
              </div>
              
              {/* AI-powered Game Plans */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">AI-powered Game Plans</p>
                    <p className="text-xs text-gray-600">
                      {isProUser ? 'Unlimited' : `${subscriptionService.getGamePlanGenerationCount(profile || null)}/${ROOKIE_LIMITS.GAME_PLANS} this month`}
                    </p>
                  </div>
                </div>
                {!isProUser && subscriptionService.hasReachedGamePlanLimit(profile || null) && (
                  <div className="text-xs text-red-600 font-medium">Limit Reached</div>
                )}
              </div>
            </div>
            
          </div>
        {/* Sidebar – subscription card (same UI as UpgradeModal) */}
        <div className="xl:col-span-1">
          <SubscriptionCard className="mb-8" />
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={async () => {
          setShowUpgradeModal(false);
          await subscribeToPro();
        }}
      />

      {/* Footer - mobile only (desktop shows in MainLayout) */}
      <Footer className="md:hidden mt-8 pb-24" />
    </div>
  );
};

export default Dashboard;